import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const FORM_ENDPOINT = "https://formspree.io/f/mnjkbgpr";

const initialForm = { name: "", email: "", company: "", message: "" };

const ContactModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    // Light client-side email check; backend will deep-validate
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        FORM_ENDPOINT,
        {
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || "",
          message: form.message.trim(),
          _subject: "New project inquiry — weaver.video",
        },
        { headers: { Accept: "application/json" } }
      );
      toast.success("Message sent. We'll be in touch shortly.");
      setForm(initialForm);
      onOpenChange(false);
    } catch (err) {
      const fsErrors = err?.response?.data?.errors;
      let msg = "Something went wrong. Please try again.";
      if (Array.isArray(fsErrors) && fsErrors[0]?.message) {
        msg = fsErrors[0].message;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="contact-modal"
        className="bg-[#0A0A0A] border-white/10 text-white max-w-xl sm:rounded-none p-0"
      >
        <div className="p-8 sm:p-10">
          <DialogHeader>
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
              — Make Contact
            </span>
            <DialogTitle
              className="mt-3 text-3xl sm:text-4xl font-black uppercase tracking-tight leading-none"
              data-testid="contact-modal-title"
            >
              Tell us about
              <br />
              <span className="text-zinc-500">your project.</span>
            </DialogTitle>
            <DialogDescription className="mt-4 text-sm font-light text-zinc-400 leading-relaxed">
              Drop us a note and a real human at Weaver Media will reply within
              one business day.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={handleSubmit}
            noValidate
            className="mt-8 space-y-5"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label
                  className="block text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2"
                  htmlFor="contact-name"
                >
                  Name *
                </label>
                <input
                  id="contact-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange("name")}
                  data-testid="contact-input-name"
                  className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-base font-light text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors"
                  placeholder="Your name"
                  autoComplete="name"
                />
              </div>
              <div>
                <label
                  className="block text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2"
                  htmlFor="contact-email"
                >
                  Email *
                </label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange("email")}
                  data-testid="contact-input-email"
                  className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-base font-light text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors"
                  placeholder="you@brand.com"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label
                className="block text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2"
                htmlFor="contact-company"
              >
                Company
              </label>
              <input
                id="contact-company"
                type="text"
                value={form.company}
                onChange={handleChange("company")}
                data-testid="contact-input-company"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-base font-light text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors"
                placeholder="Optional"
                autoComplete="organization"
              />
            </div>

            <div>
              <label
                className="block text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2"
                htmlFor="contact-message"
              >
                Project Brief *
              </label>
              <textarea
                id="contact-message"
                required
                rows={4}
                value={form.message}
                onChange={handleChange("message")}
                data-testid="contact-input-message"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-base font-light text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors resize-none"
                placeholder="Tell us what you'd like to make..."
              />
            </div>

            <div className="pt-4 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="group inline-flex items-center gap-3 px-9 py-4 bg-white text-black font-bold text-xs uppercase tracking-[0.25em] hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Message
                    <ArrowRight
                      size={16}
                      className="transition-transform duration-500 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
